<script setup lang="ts">
import { AssetType, SellingMethod } from '~/apiClient';

// COMPOSABLES
const { tt } = useTypedI18n();
useSeoMeta({
    title: tt('seo.reasonsBuyBusAuction.title'),
    ogTitle: tt('seo.reasonsBuyBusAuction.title'),
    description: tt('seo.reasonsBuyBusAuction.description'),
    ogDescription: tt('seo.reasonsBuyBusAuction.description'),
});
const { formatSlpRoute } = useSlpRouteUtils();
const localePath = useLocalePath();

// HANDLES
const onHeroCtaClick = (): void => {
    scrollIntoView('feature');
};
</script>

<template>
    <div class="mb-12 flex flex-col gap-y-12 sm:mb-16 sm:gap-y-16 xl:mb-28 xl:gap-y-28">
        <BlockHeroLinedUp
            :title="tt('reasonsBuyBusAuction.hero.title')"
            :media="{ type: 'image', src: '/reasons-buy-bus-auction/hero.webp' }"
            :button="{ trailingIcon: 'lucide:move-down', label: tt('reasonsBuyBusAuction.hero.cta') }"
            @cta-click="onHeroCtaClick"
        >
            <template #description>
                <div class="max-w-[960px]">
                    <p class="text-muted pb-8 text-lg font-medium">{{ tt('reasonsBuyBusAuction.hero.description1') }}</p>
                    <BaseI18nT keypath="reasonsBuyBusAuction.hero.description2.base" tag="p" class="text-muted pb-8 text-lg font-medium">
                        <template #bold>
                            <span class="font-bold"> {{ tt('reasonsBuyBusAuction.hero.description2.bold') }} </span>
                        </template>
                    </BaseI18nT>
                    <p class="text-muted text-lg font-bold">{{ tt('reasonsBuyBusAuction.hero.description3') }}</p>
                </div>
            </template>
        </BlockHeroLinedUp>
        <div id="feature" class="space-y-14 md:space-y-16">
            <BlockSectionFeatureFleequid
                :title="tt('reasonsBuyBusAuction.sectionWhyBuy.item1.title')"
                :fleequid="tt('reasonsBuyBusAuction.sectionWhyBuy.item1.fleequid')"
            >
                <template #description>
                    <BaseI18nT keypath="reasonsBuyBusAuction.sectionWhyBuy.item1.description.base" tag="p" class="leading-6 font-normal">
                        <template #bold>
                            <span class="font-bold"> {{ tt('reasonsBuyBusAuction.sectionWhyBuy.item1.description.bold') }} </span>
                        </template>
                    </BaseI18nT>
                </template>
            </BlockSectionFeatureFleequid>
            <BlockSectionFeatureFleequid
                :title="tt('reasonsBuyBusAuction.sectionWhyBuy.item2.title')"
                :fleequid="tt('reasonsBuyBusAuction.sectionWhyBuy.item2.fleequid')"
                :description="tt('reasonsBuyBusAuction.sectionWhyBuy.item2.description')"
            />
            <BlockSectionFeatureFleequid
                :title="tt('reasonsBuyBusAuction.sectionWhyBuy.item3.title')"
                :fleequid="tt('reasonsBuyBusAuction.sectionWhyBuy.item3.fleequid')"
                :description="tt('reasonsBuyBusAuction.sectionWhyBuy.item3.description')"
            />
            <BlockSectionFeatureFleequid
                :title="tt('reasonsBuyBusAuction.sectionWhyBuy.item4.title')"
                :fleequid="tt('reasonsBuyBusAuction.sectionWhyBuy.item4.fleequid')"
                :description="tt('reasonsBuyBusAuction.sectionWhyBuy.item4.description')"
            />
            <BaseWidthContainer class="text-center">
                <UButton
                    color="primary"
                    size="xl"
                    trailing-icon="lucide:chevron-right"
                    :label="tt('reasonsBuyBusAuction.buyCta')"
                    :to="localePath(formatSlpRoute({ sellingMethod: SellingMethod.Auction, assetType: AssetType.Bus }))"
                />
            </BaseWidthContainer>
        </div>
        <BlockSectionFullWidth variant="neutral">
            <template #description>
                <div class="flex flex-col gap-y-8 lg:grid lg:grid-cols-2 lg:gap-x-12">
                    <div class="space-y-8">
                        <h3 class="text-primary-800 text-3xl leading-9 font-semibold">{{ tt('reasonsBuyBusAuction.sectionTrust.item1.title') }}</h3>
                        <BaseI18nT keypath="reasonsBuyBusAuction.sectionTrust.item1.description.base" tag="p" class="">
                            <template #primary>
                                <span class="text-primary font-bold"> {{ tt('reasonsBuyBusAuction.sectionTrust.item1.description.primary') }} </span>
                            </template>
                            <template #bold>
                                <span class="font-bold"> {{ tt('reasonsBuyBusAuction.sectionTrust.item1.description.bold') }} </span>
                            </template>
                        </BaseI18nT>
                    </div>
                    <div class="space-y-8">
                        <h3 class="text-primary-800 text-3xl leading-9 font-semibold">{{ tt('reasonsBuyBusAuction.sectionTrust.item2.title') }}</h3>
                        <BaseI18nT keypath="reasonsBuyBusAuction.sectionTrust.item2.description.base" tag="p" class="">
                            <template #bold>
                                <span class="font-bold"> {{ tt('reasonsBuyBusAuction.sectionTrust.item2.description.bold') }} </span>
                            </template>
                        </BaseI18nT>
                    </div>
                </div>
            </template>
        </BlockSectionFullWidth>
        <BlockSectionFullWidth
            :button="{
                trailingIcon: 'lucide:chevron-right',
                label: tt('reasonsBuyBusAuction.sectionFindNextBus.cta'),
                to: localePath(formatSlpRoute({ sellingMethod: SellingMethod.Auction, assetType: AssetType.Bus })),
            }"
            :title="tt('reasonsBuyBusAuction.sectionFindNextBus.title')"
        />
        <BlockSection :title="tt('reasonsBuyBusAuction.faq.title')">
            <BaseAccordion
                size="xl"
                :items="[
                    { label: tt('reasonsBuyBusAuction.faq.items.item1.title'), content: tt('reasonsBuyBusAuction.faq.items.item1.content') },
                    { label: tt('reasonsBuyBusAuction.faq.items.item2.title'), content: tt('reasonsBuyBusAuction.faq.items.item2.content') },
                    { label: tt('reasonsBuyBusAuction.faq.items.item3.title'), content: tt('reasonsBuyBusAuction.faq.items.item3.content') },
                ]"
            />
        </BlockSection>
    </div>
</template>
